import { useMemo, useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import VideoCard from "@/components/base/VideoCard";
import { usePageMeta } from "@/hooks/usePageMeta";
import { useFavorites } from "@/hooks/useFavorites";
import { categoryMeta, videos } from "@/mocks/videos";
import { formatPlayCount, typeLabel } from "@/utils/format";
import VideoPlayer from "./components/VideoPlayer";

export default function DetailPage() {
  const { id } = useParams<{ id: string }>();
  const video = videos.find((v) => v.id === id);
  const { isFavorite, toggleFavorite } = useFavorites();
  const [toast, setToast] = useState("");

  usePageMeta({
    title: video ? `${video.title} - 在线视频` : "视频详情 - 在线视频",
    description: video ? video.description : "在线视频详情页",
    keywords: video
      ? `${video.title},${typeLabel(video.type)},在线观看,在线视频`
      : "在线视频,视频详情",
  });

  const related = useMemo(() => {
    if (!video) return [];
    return videos
      .filter((v) => v.type === video.type && v.id !== video.id)
      .sort((a, b) => b.playCount - a.playCount)
      .slice(0, 5);
  }, [video]);

  if (!video) {
    return <Navigate to="/404" replace />;
  }

  const meta = categoryMeta[video.type];
  const favored = isFavorite(video.id);

  const showToast = (msg: string) => {
    setToast(msg);
    window.setTimeout(() => setToast(""), 2000);
  };

  const handleFavorite = () => {
    const added = toggleFavorite(video.id);
    showToast(added ? "已加入收藏" : "已取消收藏");
  };

  const handleShare = async () => {
    const url = window.location.href;
    try {
      await navigator.clipboard.writeText(url);
      showToast("链接已复制，快分享给朋友吧");
    } catch {
      showToast("复制失败，请手动复制地址栏链接");
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-background-50">
      <SiteHeader />
      <main className="flex-1 w-full px-4 md:px-8 py-6">
        <div className="max-w-[1200px] mx-auto">
          {/* Breadcrumb */}
          <nav
            aria-label="面包屑"
            className="flex items-center flex-wrap gap-1.5 text-sm text-foreground-500 mb-5"
          >
            <Link to="/" className="hover:text-primary-600 cursor-pointer">
              首页
            </Link>
            <i className="ri-arrow-right-s-line"></i>
            <Link
              to={`/category/${meta.slug}`}
              className="hover:text-primary-600 cursor-pointer"
            >
              {meta.label}
            </Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-foreground-800 font-medium line-clamp-1">
              {video.title}
            </span>
          </nav>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Main */}
            <div className="lg:col-span-2">
              <VideoPlayer poster={video.backdrop} title={video.title} />

              <div className="mt-6">
                <h1 className="text-2xl md:text-3xl font-bold text-foreground-950">
                  {video.title}
                </h1>
                <p className="mt-2 text-base text-foreground-500">
                  {video.subtitle}
                </p>

                {/* Meta bar */}
                <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-foreground-600">
                  <span className="px-2 py-0.5 rounded bg-primary-50 text-primary-700 font-medium">
                    {typeLabel(video.type)}
                  </span>
                  <span>上映：{video.year}</span>
                  <span>地区：{video.region}</span>
                  <span>时长：{video.duration}</span>
                  <span>更新：{video.updateDate}</span>
                </div>

                <div className="mt-4 flex flex-wrap items-center gap-5 text-sm">
                  <span className="flex items-center gap-1.5 text-accent-600 font-semibold">
                    <i className="ri-star-fill"></i>
                    {video.rating.toFixed(1)} 分
                  </span>
                  <span className="flex items-center gap-1.5 text-foreground-500">
                    <i className="ri-fire-line text-accent-500"></i>
                    {formatPlayCount(video.playCount)} 次播放
                  </span>
                </div>

                {/* Actions */}
                <div className="mt-5 flex flex-wrap items-center gap-3">
                  <button
                    type="button"
                    onClick={handleFavorite}
                    className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-md font-medium whitespace-nowrap transition-colors cursor-pointer ${
                      favored
                        ? "bg-accent-500 text-background-50 dark:text-foreground-950"
                        : "bg-background-100 text-foreground-700 hover:bg-background-200"
                    }`}
                  >
                    <i className={favored ? "ri-heart-fill" : "ri-heart-line"}></i>
                    {favored ? "已收藏" : "收藏"}
                  </button>
                  <button
                    type="button"
                    onClick={handleShare}
                    className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-background-100 text-foreground-700 font-medium whitespace-nowrap hover:bg-background-200 transition-colors cursor-pointer"
                  >
                    <i className="ri-share-forward-line"></i>
                    分享
                  </button>
                  <Link
                    to={`/category/${meta.slug}`}
                    className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md border border-background-300 text-foreground-700 font-medium whitespace-nowrap hover:border-primary-400 hover:text-primary-600 transition-colors cursor-pointer"
                  >
                    <i className="ri-arrow-left-line"></i>
                    返回{meta.label}
                  </Link>
                </div>

                {/* Tags */}
                <div className="mt-5 flex flex-wrap gap-2">
                  {video.tags.map((tag) => (
                    <span
                      key={tag}
                      className="px-2.5 py-1 rounded-full bg-secondary-100 text-secondary-900 text-xs font-medium"
                    >
                      {tag}
                    </span>
                  ))}
                </div>

                {/* Description */}
                <section className="mt-7">
                  <h2 className="text-lg font-bold text-foreground-950 mb-3">
                    内容简介
                  </h2>
                  <p className="text-sm md:text-base leading-relaxed text-foreground-700">
                    {video.description}
                  </p>
                  <p className="mt-3 text-xs text-foreground-400 flex items-start gap-1.5">
                    <i className="ri-information-line mt-0.5"></i>
                    当前播放为前端演示，页面不包含真实视频源，后续接入正式服务即可在线观看。
                  </p>
                </section>
              </div>
            </div>

            {/* Sidebar related */}
            <aside className="lg:col-span-1">
              <div className="lg:sticky lg:top-20">
                <div className="flex items-center gap-2.5 mb-4">
                  <span className="w-1.5 h-6 rounded-full bg-accent-500"></span>
                  <h2 className="text-lg font-bold text-foreground-950">
                    相关推荐
                  </h2>
                </div>
                <div className="grid grid-cols-2 lg:grid-cols-1 gap-3 md:gap-4">
                  {related.map((item) => (
                    <Link
                      key={item.id}
                      to={`/detail/${item.id}`}
                      className="group flex gap-3 p-2 rounded-lg bg-background-50 border border-background-200 hover:border-primary-300 transition-colors cursor-pointer"
                    >
                      <span className="relative w-16 h-24 shrink-0 rounded-md overflow-hidden bg-background-200">
                        <img
                          src={item.cover}
                          alt={`${item.title} 封面`}
                          title={`${item.title} - 在线视频`}
                          className="w-full h-full object-cover object-top"
                        />
                      </span>
                      <span className="min-w-0 flex flex-col justify-center">
                        <span className="text-sm font-semibold text-foreground-950 line-clamp-1 group-hover:text-primary-600 transition-colors">
                          {item.title}
                        </span>
                        <span className="mt-1 text-xs text-foreground-500 line-clamp-1">
                          {item.subtitle}
                        </span>
                        <span className="mt-1.5 text-xs text-foreground-500">
                          {item.year} · {item.region}
                        </span>
                      </span>
                    </Link>
                  ))}
                </div>

                <Link
                  to={`/category/${meta.slug}`}
                  className="mt-4 flex items-center justify-center gap-2 w-full px-4 py-3 rounded-md bg-secondary-500 text-background-50 dark:text-foreground-950 font-medium whitespace-nowrap hover:bg-secondary-600 transition-colors cursor-pointer"
                >
                  查看全部{meta.label}
                  <i className="ri-arrow-right-line"></i>
                </Link>
              </div>
            </aside>
          </div>

          {/* Related grid (desktop quality) */}
          <section className="mt-12">
            <div className="flex items-center gap-2.5 mb-5">
              <span className="w-1.5 h-6 rounded-full bg-primary-500"></span>
              <h2 className="text-xl font-bold text-foreground-950">
                更多{meta.label}推荐
              </h2>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 md:gap-4">
              {videos
                .filter((v) => v.type === video.type && v.id !== video.id)
                .slice(0, 5)
                .map((item) => (
                  <VideoCard key={item.id} video={item} />
                ))}
            </div>
          </section>
        </div>
      </main>
      <SiteFooter />

      {/* Toast */}
      {toast && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 px-5 py-3 rounded-md bg-foreground-950 text-background-50 text-sm font-medium animate-fade-up">
          {toast}
        </div>
      )}
    </div>
  );
}